import pMap from "p-map";
import type { LearningSet } from "../../../interfaces/AthenaResources";
import getAssetUrls from "../common/getAssetUrls";
import type CachedModulesSet from "./CachedModulesSet";
import type ModuleAssetsCache from "./ModuleAssetsCache";
import type ModuleCachingState from "./ModuleCachingState";
import { CacheOperationError, ModuleCachingStatus } from "./ModuleCachingState";
import ModuleCachingStateChangeEvent from "./ModuleCachingStateChangeEvent";

export default class ModuleCacheManager extends EventTarget {
  constructor(
    private moduleId: string,
    private appVersion: string,
    private baseAssetsUrl: string,
    private getLearningSets: (
      moduleId: string,
      abortSignal?: AbortSignal,
    ) => Promise<{
      [learningSetId: string]: LearningSet;
    }>,
    private moduleAssetsCache: ModuleAssetsCache,
    private cachedModulesSet: CachedModulesSet,
    /**
     * Limit of concurrently executing asset fetching-and-caching
     * operations.
     *
     * Browsers limit the number of active outgoing connections to the same
     * origin (to 6 in most cases), placing other requests in a queue. To
     * avoid timeout errors, and since anyway having much higher concurrency
     * on our part doesn't yield any benefit, we also limit the concurrency
     * with which we cache each asset. This constructor parameter configures
     * that limit.
     *
     * We default to using a slightly higher concurrency limit than the
     * browser outgoing requests limit, as it might make things a bit more
     * efficient, leveraging the browser request queue (but not abusing it,
     * which might lead to timeouts). Why? When one request is done, the
     * browser has the next one queued already and can start it right away,
     * without having to wait for our bit of js to resume execution and
     * trigger the next request. (Note: only an hypothesis, not actually
     * tested.)
     */
    private cachingConcurrencyLimit = 6 + 2,
  ) {
    super();
  }

  private abortController: AbortController | null = null;
  private moduleCachingState: ModuleCachingState = {
    status: this.cachedModulesSet.has(this.moduleId)
      ? ModuleCachingStatus.Cached
      : ModuleCachingStatus.NotCached,
  };

  getModuleCachingState(): ModuleCachingState {
    return this.moduleCachingState;
  }

  async cache() {
    try {
      this.abortController = new AbortController();

      // Init the state to Caching. We don't know the assets count yet, so
      // for the moment it stays null.
      this.setModuleCachingState({
        status: ModuleCachingStatus.Caching,
        assetsCount: null,
        cachedAssetsCount: 0,
      });

      // Get the learning sets of the module, and derive from them the
      // urls of the assets we need to cache.
      const learningSets = await this.getLearningSets(
        this.moduleId,
        this.abortController?.signal,
      );
      const assetUrls = getAssetUrls(
        learningSets,
        this.appVersion,
        this.baseAssetsUrl,
      );

      // Now that we know which assets we need to cache, we can update the
      // assets count.
      this.setModuleCachingState({
        status: ModuleCachingStatus.Caching,
        assetsCount: assetUrls.length,
        cachedAssetsCount: 0,
      });

      // Cache each asset. See comment on cachingConcurrencyLimit
      // constructor parameter for an explanation of why concurrency is
      // limited.
      await pMap(
        assetUrls,
        (assetUrl) => this.cacheAssetAndUpdateState(assetUrl),
        { concurrency: this.cachingConcurrencyLimit },
      );

      // Everything went well, add the module to the cachedModuleSet and
      // update the state to Cached.
      this.cachedModulesSet.add(this.moduleId);
      this.setModuleCachingState({ status: ModuleCachingStatus.Cached });
    } catch (error) {
      // We end-up here in two cases:
      // 1. The operation was aborted.
      // 2. getLearningSets failed (but not because it was aborted).
      await this.uncache(
        error instanceof DOMException &&
          (error.name === "AbortError" ||
            // Legacy check using the deprecated code property
            error.code === error.ABORT_ERR)
          ? CacheOperationError.Aborted
          : CacheOperationError.Failed,
      );
    } finally {
      this.abortController = null;
    }
  }

  async uncache(error?: CacheOperationError) {
    await this.moduleAssetsCache.uncacheAllAssets(this.moduleId);
    this.cachedModulesSet.delete(this.moduleId);
    this.setModuleCachingState({
      status: ModuleCachingStatus.NotCached,
      error,
    });
  }

  abortCaching() {
    this.abortController?.abort();
  }

  private setModuleCachingState(moduleCachingState: ModuleCachingState) {
    this.moduleCachingState = moduleCachingState;
    this.dispatchEvent(new ModuleCachingStateChangeEvent(moduleCachingState));
  }

  private async cacheAssetAndUpdateState(assetUrl: string) {
    this.abortController?.signal.throwIfAborted();

    // Cache the asset for the module.
    await this.moduleAssetsCache
      .cacheAsset(this.moduleId, assetUrl, this.abortController?.signal)
      // When there's an error caching an asset, we can't really do
      // anything about it. It doesn't make sense to make the entire
      // module caching operation fail, which might consist of caching
      // several thousands assets, just because one asset failed to
      // download. Also, effectively, it doesn't make a difference: if
      // there's an error with one particular asset, the user wouldn't be
      // able to download that asset no matter if they're online or not,
      // so we might as well ignore that asset and not consider it when
      // answering the question "is this module cached for offline use?".
      .catch();

    // In theory, this condition should always be true, as it doesn't seem
    // to be possible to get in a state where this function is executing and
    // the state is not Caching. However, making sure doesn't hurt (we
    // wouldn't want to change the state if it's not Caching), and it also
    // avoids TypeScript complaints.
    if (this.moduleCachingState.status === ModuleCachingStatus.Caching) {
      this.setModuleCachingState({
        ...this.moduleCachingState,
        cachedAssetsCount: this.moduleCachingState.cachedAssetsCount + 1,
      });
    }
  }
}
